"use client";
import { FloatingPortal } from "@floating-ui/react";
import { useTranslations } from "next-intl";
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { VictoryThemeDefinition } from "victory";

import GroupChart from "@/components/charts/group_chart";
import MultipleChoiceChart from "@/components/charts/multiple_choice_chart";
import MCPredictionsTooltip from "@/components/charts/primitives/mc_predictions_tooltip";
import { GroupTimelineMarker } from "@/components/charts/primitives/timeline_markers/types";
import { getEffectiveVisibleCount } from "@/constants/questions";
import { useAuth } from "@/contexts/auth_context";
import useChartTooltip from "@/hooks/use_chart_tooltip";
import {
  TickFormat,
  TimelineChartZoomOption,
  TimelineYDomainOptions,
} from "@/types/charts";
import { ChoiceItem, ChoiceTooltipItem } from "@/types/choices";
import { ForecastAvailability, QuestionType, Scaling } from "@/types/question";
import cn from "@/utils/core/cn";
import { buildChoicesWithOthers } from "@/utils/questions/choices";

import ChoicesOptionsDropdown from "./choices_options_dropdown";
import CompactLegendBar from "./compact_legend_bar";

type Props = {
  choiceItems: ChoiceItem[];
  tooltipTitle?: string;
  tooltipChoices: ChoiceTooltipItem[];
  tooltipUserChoices?: ChoiceTooltipItem[];
  forecastersCount?: number | null;
  onChoiceItemsUpdate: (choiceItems: ChoiceItem[]) => void;
  timestamps: number[];
  onCursorChange?: (value: number, format: TickFormat) => void;
  openTime?: number | null;
  actualCloseTime?: number | null;
  isClosed?: boolean;
  hideCP?: boolean;
  cursorTimestamp?: number | null;
  title?: ReactNode;
  yLabel?: string;
  questionType?: QuestionType;
  scaling?: Scaling;
  defaultZoom?: TimelineChartZoomOption;

  withLegend?: boolean;
  chartHeight?: number;
  chartTheme?: VictoryThemeDefinition;
  embedMode?: boolean;
  className?: string;
  forecastAvailability?: ForecastAvailability;
  timelineMarkers?: GroupTimelineMarker[];
  activeTimelineMarkerId?: string | null;
  onTimelineMarkerEnter?: (marker: GroupTimelineMarker) => void;
  onTimelineMarkerLeave?: (marker: GroupTimelineMarker) => void;
  withHighlightArea?: boolean;
  withHighlightEndpoint?: boolean;
  hideTooltip?: boolean;
  headerLeft?: ReactNode;
  hideChartTitle?: boolean;
  yDomainOptions?: TimelineYDomainOptions;
};

const MultiChoicesChartView: FC<Props> = ({
  choiceItems,
  tooltipTitle,
  tooltipChoices,
  tooltipUserChoices,
  forecastersCount,
  cursorTimestamp,
  onChoiceItemsUpdate,
  timestamps,
  onCursorChange,
  openTime,
  actualCloseTime,
  isClosed,
  hideCP,

  title,
  yLabel,
  questionType,
  scaling,
  defaultZoom,

  withLegend = true,
  chartHeight,
  chartTheme,
  embedMode = false,
  className,
  forecastAvailability,
  timelineMarkers,
  activeTimelineMarkerId,
  onTimelineMarkerEnter,
  onTimelineMarkerLeave,
  withHighlightArea = true,
  withHighlightEndpoint = false,
  hideTooltip = false,
  headerLeft,
  hideChartTitle = false,
  yDomainOptions,
}) => {
  const { user } = useAuth();
  const [isChartReady, setIsChartReady] = useState(false);
  const [isCursorOverLegend, setIsCursorOverLegend] = useState(false);
  const legendEnterProps = {
    onMouseEnter: () => setIsCursorOverLegend(true),
    onMouseLeave: () => setIsCursorOverLegend(false),
    onTouchStart: () => setIsCursorOverLegend(true),
    onTouchEnd: () => setIsCursorOverLegend(false),
    onTouchCancel: () => setIsCursorOverLegend(false),
  };
  const handleChartReady = useCallback(() => setIsChartReady(true), []);
  const t = useTranslations();
  const [touchCoords, setTouchCoords] = useState<
    { x: number; y: number } | undefined
  >();

  const isMC = questionType === QuestionType.MultipleChoice;

  const maxPrimary = embedMode
    ? 2
    : getEffectiveVisibleCount(choiceItems.length);

  const normalizedInitRef = useRef(false);
  useEffect(() => {
    if (normalizedInitRef.current) return;
    if (!choiceItems.length) return;
    const updated = choiceItems.map((it, idx) =>
      idx >= maxPrimary ? { ...it, active: false } : it
    );
    const changed = updated.some(
      (it, i) => it.active !== choiceItems[i]?.active
    );
    if (changed) onChoiceItemsUpdate(updated);
    normalizedInitRef.current = true;
  }, [choiceItems, maxPrimary, onChoiceItemsUpdate]);

  const {
    isActive: isTooltipActive,
    getReferenceProps,
    getFloatingProps,
    refs,
    floatingStyles,
  } = useChartTooltip({
    x: touchCoords?.x,
    y: touchCoords?.y,
    placement: touchCoords ? "top" : "left",
    forceOpen: !!touchCoords,
  });
  const tooltipStyle = useMemo<React.CSSProperties>(() => {
    if (!touchCoords) return floatingStyles;
    return {
      position: "fixed",
      top: touchCoords.y - 24,
      left: touchCoords.x,
      transform: "translate(-50%, -100%)",
      zIndex: 100,
    };
  }, [touchCoords, floatingStyles]);

  const attachRef = useCallback(
    (node: HTMLElement | null) => {
      if (node) refs.setReference(node);
    },
    [refs]
  );

  const handleChoiceChange = useCallback(
    (choice: string, checked: boolean) => {
      onChoiceItemsUpdate(
        choiceItems.map((item) =>
          item.choice === choice
            ? { ...item, active: checked, highlighted: false }
            : item
        )
      );
    },
    [choiceItems, onChoiceItemsUpdate]
  );

  const handleChoiceHighlight = useCallback(
    (choice: string, highlighted: boolean) => {
      onChoiceItemsUpdate(
        choiceItems.map((item) =>
          item.choice === choice ? { ...item, highlighted } : item
        )
      );
    },
    [choiceItems, onChoiceItemsUpdate]
  );

  const handleToggleAll = useCallback(
    (checked: boolean) => {
      onChoiceItemsUpdate(
        choiceItems.map((item) => ({
          ...item,
          active: checked,
          highlighted: false,
        }))
      );
    },
    [choiceItems, onChoiceItemsUpdate]
  );

  const chartChoiceItems = useMemo(
    () => (isMC ? buildChoicesWithOthers(choiceItems) : choiceItems),
    [isMC, choiceItems]
  );

  const totalActiveCount = useMemo(
    () => choiceItems.filter((c) => c.active).length,
    [choiceItems]
  );

  const canTimeline = isMC && totalActiveCount === 1;

  const [timelineMode, setTimelineMode] = useState(false);

  useEffect(() => {
    if (!canTimeline && timelineMode) setTimelineMode(false);
  }, [canTimeline, timelineMode]);

  const useBinaryView = canTimeline && timelineMode;

  const singleActive = useMemo(
    () => getSingleActive(choiceItems),
    [choiceItems]
  );

  const binaryChoiceItems = useMemo(
    () =>
      singleActive
        ? [{ ...singleActive, color: singleActive.color, highlighted: true }]
        : [],
    [singleActive]
  );

  const commonChartProps = {
    actualCloseTime,
    timestamps,
    hideCP,
    yLabel: embedMode ? undefined : yLabel,
    onChartReady: handleChartReady,
    onCursorChange,
    scaling,
    isClosed,
    extraTheme: chartTheme,
    height: chartHeight,
    withZoomPicker: true,
    defaultZoom: resolveDefaultZoom(defaultZoom, !!user),
    openTime,
    forecastAvailability,
    attachRef,
    withHighlightArea,
    withHighlightEndpoint,
    yDomainOptions,
    headerExtra:
      !embedMode && choiceItems.length > 1 ? (
        <ChoicesOptionsDropdown
          choices={choiceItems}
          onChoiceChange={handleChoiceChange}
          onToggleAll={handleToggleAll}
        />
      ) : undefined,
  } as const;

  return (
    <div
      className={cn(
        "flex w-full flex-col",
        className,
        isChartReady ? "opacity-100" : "opacity-0"
      )}
    >
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="relative touch-none"
        onTouchStartCapture={(e) => {
          const touch = e.touches[0];
          if (touch) setTouchCoords({ x: touch.clientX, y: touch.clientY });
        }}
        onTouchMoveCapture={(e) => {
          const touch = e.touches[0];
          if (touch) setTouchCoords({ x: touch.clientX, y: touch.clientY });
        }}
        onTouchEndCapture={() => setTouchCoords(undefined)}
        onTouchCancelCapture={() => setTouchCoords(undefined)}
        onMouseMoveCapture={() => setTouchCoords(undefined)}
      >
        {useBinaryView ? (
          <GroupChart
            {...commonChartProps}
            cursorTimestamp={null}
            questionType={QuestionType.Binary}
            isEmptyDomain={
              !!forecastAvailability?.isEmpty ||
              !!forecastAvailability?.cpRevealsOn
            }
            choiceItems={binaryChoiceItems}
            chartTitle={!embedMode && !hideChartTitle ? title : undefined}
            timelineMarkers={timelineMarkers}
            activeTimelineMarkerId={activeTimelineMarkerId}
            onTimelineMarkerEnter={onTimelineMarkerEnter}
            onTimelineMarkerLeave={onTimelineMarkerLeave}
            headerLeft={
              withLegend ? (
                <div {...legendEnterProps}>
                  <CompactLegendBar
                    items={choiceItems}
                    questionType={QuestionType.MultipleChoice}
                    onChoiceChange={handleChoiceChange}
                    onChoiceHighlight={handleChoiceHighlight}
                    hideCP={hideCP}
                  />
                </div>
              ) : (
                headerLeft
              )
            }
            forceShowLinePoints={!embedMode}
          />
        ) : isMC ? (
          <MultipleChoiceChart
            {...commonChartProps}
            isEmbedded={embedMode}
            chartTitle={!embedMode && !hideChartTitle ? title : undefined}
            choiceItems={chartChoiceItems}
            headerLeft={
              withLegend ? (
                <div {...legendEnterProps}>
                  <CompactLegendBar
                    items={choiceItems}
                    questionType={QuestionType.MultipleChoice}
                    onChoiceChange={handleChoiceChange}
                    onChoiceHighlight={handleChoiceHighlight}
                    hideCP={hideCP}
                  />
                </div>
              ) : (
                headerLeft
              )
            }
          />
        ) : (
          <GroupChart
            {...commonChartProps}
            questionType={questionType}
            isEmptyDomain={
              !!forecastAvailability?.isEmpty ||
              !!forecastAvailability?.cpRevealsOn
            }
            cursorTimestamp={cursorTimestamp}
            choiceItems={choiceItems}
            chartTitle={!embedMode && !hideChartTitle ? title : undefined}
            timelineMarkers={timelineMarkers}
            activeTimelineMarkerId={activeTimelineMarkerId}
            onTimelineMarkerEnter={onTimelineMarkerEnter}
            onTimelineMarkerLeave={onTimelineMarkerLeave}
            headerLeft={
              withLegend ? (
                <div {...legendEnterProps}>
                  <CompactLegendBar
                    items={choiceItems}
                    questionType={questionType ?? QuestionType.Binary}
                    onChoiceChange={handleChoiceChange}
                    onChoiceHighlight={handleChoiceHighlight}
                    hideCP={hideCP}
                  />
                </div>
              ) : (
                headerLeft
              )
            }
            forceShowLinePoints={!embedMode}
          />
        )}
      </div>

      {isTooltipActive &&
        !hideTooltip &&
        !isCursorOverLegend &&
        !hideCP &&
        !forecastAvailability?.cpRevealsOn &&
        !activeTimelineMarkerId &&
        (tooltipChoices.length > 0 ||
          !!tooltipUserChoices?.length ||
          !!forecastAvailability?.isEmpty) && (
          <FloatingPortal>
            <div
              className="pointer-events-none z-[100] rounded bg-gray-0 leading-4 shadow-lg dark:bg-gray-0-dark"
              ref={refs.setFloating}
              style={tooltipStyle}
              {...getFloatingProps()}
            >
              <MCPredictionsTooltip
                title={tooltipTitle}
                communityPredictions={tooltipChoices}
                userPredictions={tooltipUserChoices}
                showMeColumn={
                  !!tooltipUserChoices?.length ||
                  !!forecastAvailability?.cpRevealsOn ||
                  !!forecastAvailability?.isEmpty
                }
                FooterRow={
                  forecastersCount !== null &&
                  forecastersCount !== undefined ? (
                    <tr className="border-t border-gray-300 dark:border-gray-300-dark">
                      <th
                        className="px-3 pb-1 pt-2 text-left text-sm font-normal text-gray-800 dark:text-gray-800-dark"
                        colSpan={2}
                      >
                        {t("activeForecastersLabel")}
                      </th>
                      <td
                        className="pr-5 pt-1 text-right text-sm font-normal tabular-nums text-gray-700 dark:text-gray-700-dark"
                        colSpan={2}
                      >
                        {forecastersCount}
                      </td>
                    </tr>
                  ) : null
                }
              />
            </div>
          </FloatingPortal>
        )}
    </div>
  );
};

function resolveDefaultZoom(
  explicit: TimelineChartZoomOption | undefined,
  hasUser: boolean
) {
  return (
    explicit ??
    (hasUser ? TimelineChartZoomOption.All : TimelineChartZoomOption.TwoMonths)
  );
}

function getSingleActive(all: ChoiceItem[]): ChoiceItem | null {
  const actives = all.filter((c) => c.active);
  return actives.length === 1 ? actives[0] ?? null : null;
}

export default MultiChoicesChartView;
